/**
 * Animus Quarter — the exterior district.
 *
 * A T-junction: a 76 m High Street running east-west, met from the south by a
 * 24 m approach stem, with the anchor building closing the vista at the top of
 * the T. Every number here comes from real street-design standards (NACTO
 * sidewalk zones, Manual for Streets carriageway widths, Gehl's facade rhythm)
 * adjusted for two facts about this game:
 *
 *  1. The player walks at 2.6 m/s — 1.87x real pace — so a facade built to
 *     Gehl's measured 5-second rhythm would strobe. Hence a 6 m detail bay
 *     nested inside a 12-17 m plot identity (4.6-6.5 s per building).
 *  2. The camera's 45 deg VERTICAL fov means the player only ever sees
 *     `1.65 + 0.414 * distance` metres of a facade — 5.4 m of a 13 m wall from
 *     mid-carriageway. So enclosure has to come from NARROWING rather than
 *     height (H:W 1:1.38, tighter than the real-world 1:1.5-1:3 optimum), and
 *     everything readable must live below 5.5 m. Above that is silhouette.
 *
 * Coordinates match the interior convention: +X east, +Z south, 1 unit = 1 m.
 */

// ── Overall extent ────────────────────────────────────────────────────────

export const DISTRICT_MIN_X = -50
export const DISTRICT_MAX_X = 62
// The rear of the full P5 maker hall reaches -53 m; keep the authored campus
// bounds aligned with the existing -55 m ground/collider extent.
export const DISTRICT_MIN_Z = -55
export const DISTRICT_MAX_Z = 39

// ── High Street (east-west) ───────────────────────────────────────────────

/** Face-to-face width. 4.70 north footway + 7.50 carriageway + 5.80 south. */
export const HIGH_ST_WIDTH = 18.0
export const HIGH_ST_MIN_X = -38
export const HIGH_ST_MAX_X = 38
/** North building line — the anchor and its neighbours front onto this. */
export const HIGH_ST_NORTH_FACE = -9.0
/** South building line. */
export const HIGH_ST_SOUTH_FACE = 9.0

export const CARRIAGEWAY_WIDTH = 7.5
export const FOOTWAY_NORTH_WIDTH = 4.7
export const FOOTWAY_SOUTH_WIDTH = 5.8

export const CARRIAGEWAY_MIN_Z = HIGH_ST_NORTH_FACE + FOOTWAY_NORTH_WIDTH // -4.3
export const CARRIAGEWAY_MAX_Z = CARRIAGEWAY_MIN_Z + CARRIAGEWAY_WIDTH // +3.2

/** Kerb upstand — 125 mm is the British Standard for a bull-nosed kerb. */
export const KERB_HEIGHT = 0.125
/** Standard kerb unit length; the run is segmented on this so it can cull. */
export const KERB_UNIT_LENGTH = 0.915

// ── Approach stem (north-south, meets the High Street at the node) ────────

export const STEM_WIDTH = 14.0
export const STEM_MIN_X = -7
export const STEM_MAX_X = 7
export const STEM_MIN_Z = HIGH_ST_SOUTH_FACE // 9
export const STEM_MAX_Z = 33

/** The gate you enter the district through, at the far end of the stem. */
export const GATE_Z = STEM_MAX_Z
export const GATE_PIER_HALF_SPACING = 5.0
export const GATE_PIER_SIZE = 1.2
export const GATE_PIER_HEIGHT = 4.5

// ── Vertical datums ───────────────────────────────────────────────────────

/** Ground-floor height — Gehl's minimum for an active frontage. */
export const GROUND_FLOOR_H = 4.2
/** Upper-storey height. */
export const UPPER_FLOOR_H = 3.0
/**
 * Shared cornice line. Real streets vary building heights but hold a common
 * datum; that is what makes a varied terrace still read as one street.
 */
export const CORNICE_DATUM = 11.0
/** The facade rhythm module — one shopfront bay. */
export const BAY_WIDTH = 6.0

/**
 * Everything a walking player can actually read lives below this. Derived
 * from the camera's vertical fov at typical viewing distances across the
 * street; above it, a building communicates by silhouette alone.
 */
export const INFORMATION_BAND_TOP = 5.5

// ── Plots ─────────────────────────────────────────────────────────────────

export type PlotSide = 'north' | 'south-west' | 'south-east'

/**
 * Natural bounding size of each building GLB, in its own authoring units.
 * Kenney's city kits are authored around a ~1-unit module, so every model has
 * to be scaled onto its plot — and the three city kits do NOT share a unit
 * convention, which is exactly the kind of thing that silently ships a
 * two-metre-tall office block. Measured from the GLB accessor bounds.
 */
export const BUILDING_NATURAL_SIZE: Readonly<Record<string, readonly [number, number, number]>> = {
  'bld_board.glb': [0.88, 0.89, 1.09],
  'bld_ops.glb': [1.36, 2.88, 1.36],
  'bld_hq.glb': [1.36, 4.48, 1.36],
  'bld_backdrop.glb': [0.97, 1.29, 0.94],
  'bld_cafe.glb': [0.88, 1.29, 0.94],
  'bld_library.glb': [1.24, 1.68, 1.3],
  // Licensed Synty landmarks are normalized to metres by the catalog pipeline.
  'synty_city_city_hall_01.glb': [20.08585, 8.745323, 17.634109],
  'synty_city_station_03.glb': [10.776611, 5.742138, 5.909203],
  'synty_city_office_round_01.glb': [22.042628, 38.356369, 22.05155],
  'synty_apocalypse_auto_repair_01.glb': [10.800439, 7.061819, 19.734456],
  'synty_apocalypse_commercial_large_01.glb': [17.941689, 28.354311, 18.538733],
  'synty_apocalypse_industrial_medium_01.glb': [14.048105, 15.666378, 15.960254],
  'synty_city_shop_corner_01.glb': [5.376831, 3.0, 5.47128],
  'synty_town_shop_03.glb': [14.622404, 4.626159, 14.269042],
}

export interface PlotDef {
  id: string
  /** Display name on the fascia. */
  name: string
  side: PlotSide
  /** Frontage extent along the street. */
  minX: number
  maxX: number
  /** Depth away from the street (signed by side). */
  depth: number
  /** Parapet height. Varied for silhouette, held to the cornice datum. */
  height: number
  /** Whether the player can go inside. Background plots are scenery. */
  enterable: boolean
  /** Portal domain this building represents. */
  domain?: string
  /** Building GLB placed at the frontage. */
  model?: string
  /**
   * Authored model-space X/Z centre when the GLB origin is not centred.
   * Catalog dimensions alone cannot place corner-origin prefabs correctly.
   */
  modelCenter?: readonly [number, number]
  /** Additional yaw needed to present the model's authored entrance frontage. */
  modelYawOffset?: number
  /** False when a full licensed rear hall owns the remainder of the plot. */
  proceduralBackfill?: boolean
  /** Canonical street entrance, measured from the authored visible doorway. */
  entrance?: {
    /** World-space X offset from the plot frontage centre. */
    offsetX: number
    /** Zero suppresses the generic canopy when the model authors its own. */
    canopyWidth?: number
    /** Compact destination plate width; avoids a facade-wide billboard. */
    signWidth?: number
    /** Optional authored sign height. */
    signY?: number
    /** Distance the destination plate sits outward from the frontage. */
    signOffset?: number
    /** Add an Arena-authored visible door when the licensed shell has none. */
    doorPanel?: boolean
    /**
     * Set the authored door behind the plot line, inside a shell's open
     * portico. The interaction threshold remains at the street frontage.
     */
    doorInset?: number
  }
  /**
   * Tints the WALL only, turning one model into a distinct building. Kenney
   * city GLBs share a single colormap atlas, so the tint is applied through a
   * wall-column detail mask (not the whole material) — otherwise a dark tint
   * drags the already-dark glass swatch to black and the windows vanish.
   */
  tint?: readonly [number, number, number]
  /**
   * Material family knobs. Four families read across the street — brick,
   * painted render, stone, and glass-steel — and the difference is as much
   * gloss and metalness as hue: glass-steel is metallic and sharp, render is
   * flat and matte. Default to a matte, non-metal wall when unset.
   */
  metalness?: number
  gloss?: number
}

/**
 * How a building model sits on its plot.
 *
 * Scaled UNIFORMLY: one scale on all three axes, so the model keeps its
 * authored proportions. Scaling frontage and height independently visibly
 * deforms doors, mullions, floor bands, and cornices. Instead the smaller of
 * the two fits is taken and whatever does not bind becomes leftover: a lateral
 * setback (spare frontage) or a parapet band (spare height), never a stretch.
 * Depth follows the same scale, so it too stays proportional and the back
 * becomes back-of-house.
 */
export interface BuildingPlacement {
  /** Uniform scale applied to the model (equal on all three axes). */
  scale: readonly [number, number, number]
  /** World centre of the model. */
  position: readonly [number, number, number]
  /** World X/Z centre of the rotated model bounds (distinct from its origin). */
  boundsCenter: readonly [number, number]
  /** Depth the model actually occupies. */
  modelDepth: number
  /** Leftover plot depth to fill with back-of-house massing (may be 0). */
  backDepth: number
  /** Final model yaw, including any catalog frontage correction. */
  yaw: number
}

export function buildingPlacement(plot: PlotDef): BuildingPlacement | null {
  if (!plot.model) return null
  const natural = BUILDING_NATURAL_SIZE[plot.model]
  if (!natural) return null

  const frontage = plot.maxX - plot.minX
  const yaw = plotFacingYaw(plot) + (plot.modelYawOffset ?? 0)
  const quarterTurn = Math.abs(Math.round(yaw / 90)) % 2 === 1
  const naturalFrontage = quarterTurn ? natural[2] : natural[0]
  const naturalDepth = quarterTurn ? natural[0] : natural[2]
  // Uniform: the tighter of the frontage- and height-derived scales, so the
  // rotated model never overruns its plot in either dimension and is never
  // deformed. A quarter-turn swaps the authored X/Z footprint axes.
  const s = Math.min(frontage / naturalFrontage, plot.height / natural[1])
  const modelDepth = naturalDepth * s
  const cx = (plot.minX + plot.maxX) / 2
  const front = plotFrontZ(plot)
  // North plots face south (+Z is the street), south plots face north.
  const cz = plot.side === 'north' ? front - modelDepth / 2 : front + modelDepth / 2
  const [localCenterX, localCenterZ] = plot.modelCenter ?? [0, 0]
  const radians = yaw * Math.PI / 180
  const scaledX = localCenterX * s
  const scaledZ = localCenterZ * s
  const rotatedCenterX = Math.cos(radians) * scaledX + Math.sin(radians) * scaledZ
  const rotatedCenterZ = -Math.sin(radians) * scaledX + Math.cos(radians) * scaledZ

  return {
    scale: [s, s, s],
    position: [cx - rotatedCenterX, 0, cz - rotatedCenterZ],
    boundsCenter: [cx, cz],
    modelDepth,
    backDepth: Math.max(0, plot.depth - modelDepth),
    yaw,
  }
}

/**
 * The T's three frontages. North is one continuous 76 m block with the anchor
 * at its centre closing the stem's vista; the stem splits the south side into
 * two 31 m blocks, which is what gives the junction its three legible arms.
 */
// Heights are retuned for the uniform scale: a model now fills EITHER its
// frontage or its height (whichever binds first), so the height sets how much
// vertical the model gets before the leftover turns to parapet. Slender towers
// (ops, exchange) are given the height to stand up as landmarks; squat models
// (workshop) stay low under a parapet band. The set still varies the roofline
// and breaks CORNICE_DATUM, and the average holds the street's enclosure ratio.
//
// Tints drive four material families — painted render, glass-steel, brick, and
// stone — with gloss and metalness carrying as much of the identity as hue.
export const PLOTS: readonly PlotDef[] = [
  // ── North block, west of the anchor ──
  { id: 'P1', name: 'BOARD', side: 'north', minX: -38, maxX: -25, depth: 24, height: 13.0, enterable: true, domain: 'board' , model: 'synty_city_city_hall_01.glb', entrance: { offsetX: -1.72, canopyWidth: 0, signWidth: 3.8, signY: 4.4, doorPanel: true }, tint: [0.88, 0.92, 1.00], metalness: 0.1, gloss: 0.30 },
  { id: 'P2', name: 'OPS', side: 'north', minX: -25, maxX: -12, depth: 18, height: 30.0, enterable: true, domain: 'ops' , model: 'synty_city_station_03.glb', entrance: { offsetX: 3.06, canopyWidth: 0, signWidth: 3.4, signY: 4.25, doorPanel: true }, tint: [0.66, 0.78, 0.92], metalness: 0.5, gloss: 0.60 },
  // ── The anchor: today's office, rotated so its 24 m side fronts the street ──
  { id: 'P3', name: 'ANIMUS HQ', side: 'north', minX: -12, maxX: 12, depth: 36, height: 58.0, enterable: true, domain: 'hq' , model: 'bld_hq.glb', tint: [1.00, 0.94, 0.82], metalness: 0.05, gloss: 0.35 },
  // ── North block, east of the anchor ──
  { id: 'P4', name: 'EXCHANGE', side: 'north', minX: 12, maxX: 25, depth: 24, height: 38.0, enterable: true, domain: 'metrics' , model: 'synty_city_office_round_01.glb', entrance: { offsetX: 0, canopyWidth: 0, signWidth: 3.6, signY: 4.4, doorPanel: true }, tint: [0.70, 0.82, 0.98], metalness: 0.55, gloss: 0.65 },
  { id: 'P5', name: 'WORKSHOP', side: 'north', minX: 25, maxX: 38, depth: 44, height: 16.0, enterable: true, domain: 'workflows' , model: 'synty_apocalypse_auto_repair_01.glb', modelCenter: [1.044302, -1.345695], modelYawOffset: 90, proceduralBackfill: false, entrance: { offsetX: 0, canopyWidth: 0, signWidth: 3.8, signY: 4.25, signOffset: 0.18 }, tint: [1.00, 0.62, 0.45], metalness: 0.0, gloss: 0.10 },

  // ── South-west block (node corner is the cafe — corners want activity) ──
  { id: 'P9', name: '', side: 'south-west', minX: -38, maxX: -20, depth: 20, height: 12.0, enterable: false , model: 'bld_backdrop.glb', tint: [0.80, 0.80, 0.78], metalness: 0.0, gloss: 0.15 },
  { id: 'P6', name: 'CAFE', side: 'south-west', minX: -20, maxX: -7, depth: 16, height: 11.0, enterable: true, domain: 'chat' , model: 'synty_city_shop_corner_01.glb', modelCenter: [2.5, -2.452777], modelYawOffset: 180, entrance: { offsetX: 4.1, canopyWidth: 0, signWidth: 3.0, signY: 3.45 }, tint: [1.00, 0.80, 0.60], metalness: 0.0, gloss: 0.15 },

  // ── South-east block ──
  { id: 'P7', name: 'LIBRARY', side: 'south-east', minX: 7, maxX: 21, depth: 26, height: 14.0, enterable: true, domain: 'knowledge' , model: 'synty_town_shop_03.glb', entrance: { offsetX: 5.4, canopyWidth: 0, signWidth: 2.8, signY: 3.7 }, tint: [0.92, 0.86, 0.72], metalness: 0.0, gloss: 0.15 },
  { id: 'P8', name: 'DATACENTRE', side: 'south-east', minX: 21, maxX: 38, depth: 26, height: 32.0, enterable: true, domain: 'servers' , model: 'synty_apocalypse_commercial_large_01.glb', modelCenter: [0.804702, -0.282784], modelYawOffset: 180, proceduralBackfill: false, entrance: { offsetX: -5.2, canopyWidth: 0, signWidth: 3.2, signY: 4.3, doorPanel: true, doorInset: 4.0 }, tint: [0.60, 0.66, 0.80], metalness: 0.6, gloss: 0.70 },
]

/** The building that closes the view down the stem from the gate. */
export const ANCHOR_PLOT_ID = 'P3'

// ── Derived geometry ──────────────────────────────────────────────────────

export interface Rect3 {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

/** World footprint of a plot, resolved from its side and depth. */
export function plotFootprint(plot: PlotDef): Rect3 {
  if (plot.side === 'north') {
    return {
      minX: plot.minX,
      maxX: plot.maxX,
      minZ: HIGH_ST_NORTH_FACE - plot.depth,
      maxZ: HIGH_ST_NORTH_FACE,
    }
  }
  return {
    minX: plot.minX,
    maxX: plot.maxX,
    minZ: HIGH_ST_SOUTH_FACE,
    maxZ: HIGH_ST_SOUTH_FACE + plot.depth,
  }
}

/** Street-facing wall plane of a plot (where its door and fascia live). */
export function plotFrontZ(plot: PlotDef): number {
  return plot.side === 'north' ? HIGH_ST_NORTH_FACE : HIGH_ST_SOUTH_FACE
}

/** Outward normal of a plot's frontage, as a yaw in degrees. */
export function plotFacingYaw(plot: PlotDef): number {
  return plot.side === 'north' ? 180 : 0
}

/** Clear width of a building's street door (a real double entrance). */
export const DOOR_WIDTH = 2.4
/** Clear head height of a building's street door. */
export const DOOR_HEIGHT = 2.4
/**
 * Depth of the entrance recess. A door flush with a facade reads as a hole in
 * a wall; a recess gives the covered threshold that makes an entrance feel
 * like one — and is where the interior gets loaded on approach.
 */
export const DOOR_RECESS = 1.2

/** Door position for a plot — authored anchor on its street-facing frontage. */
export function plotDoorPosition(plot: PlotDef): readonly [number, number] {
  return [
    (plot.minX + plot.maxX) / 2 + (plot.entrance?.offsetX ?? 0),
    plotFrontZ(plot),
  ]
}

/** Door-to-kerb pedestrian cone kept free of furniture and vegetation. */
export function plotApproachRect(plot: PlotDef, margin = 1.2): Rect3 {
  const [doorX] = plotDoorPosition(plot)
  const halfWidth = DOOR_WIDTH / 2 + margin
  return {
    minX: doorX - halfWidth,
    maxX: doorX + halfWidth,
    minZ: plot.side === 'north' ? plotFrontZ(plot) : CARRIAGEWAY_MAX_Z,
    maxZ: plot.side === 'north' ? CARRIAGEWAY_MIN_Z : plotFrontZ(plot),
  }
}

/**
 * Number of whole shopfront bays across a frontage. Real terraces subdivide
 * a plot into repeating bays; this is what stops a 17 m facade reading as one
 * blank slab at walking speed.
 */
export function bayCount(plot: PlotDef): number {
  return Math.max(1, Math.round((plot.maxX - plot.minX) / BAY_WIDTH))
}

/** Centre X of each bay across a plot's frontage. */
export function bayCentres(plot: PlotDef): number[] {
  const n = bayCount(plot)
  const width = (plot.maxX - plot.minX) / n
  return Array.from({ length: n }, (_, i) => plot.minX + (i + 0.5) * width)
}

/**
 * A plot's solid mass, with a doorway cut through the street face.
 *
 * The naive single box for a whole footprint seals the building — which is how
 * the office ended up unreachable inside the anchor plot. This emits the two
 * flanks and the lintel over the opening instead, so the plot is still solid
 * everywhere except the door.
 */
export function plotColliderBoxes(plot: PlotDef, hasInterior = false): Array<{
  minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number
}> {
  const f = plotFootprint(plot)
  if (!plot.enterable) {
    return [{ minX: f.minX, maxX: f.maxX, minY: 0, maxY: plot.height, minZ: f.minZ, maxZ: f.maxZ }]
  }

  const [doorX] = plotDoorPosition(plot)
  const left = doorX - DOOR_WIDTH / 2
  const right = doorX + DOOR_WIDTH / 2
  const boxes = []

  if (left > f.minX) {
    boxes.push({ minX: f.minX, maxX: left, minY: 0, maxY: plot.height, minZ: f.minZ, maxZ: f.maxZ })
  }
  if (right < f.maxX) {
    boxes.push({ minX: right, maxX: f.maxX, minY: 0, maxY: plot.height, minZ: f.minZ, maxZ: f.maxZ })
  }
  // Over the opening — you can walk through, not climb over.
  boxes.push({
    minX: left, maxX: right,
    minY: DOOR_HEIGHT, maxY: plot.height,
    minZ: f.minZ, maxZ: f.maxZ,
  })

  // A doorway with no interior behind it is otherwise a corridor straight
  // through the plot and out the back of the district — where there is no
  // ground, and no way to recover but a page reload. Seal the far end.
  if (!hasInterior) {
    const backThickness = 0.3
    boxes.push(plot.side === 'north'
      ? { minX: left, maxX: right, minY: 0, maxY: plot.height, minZ: f.minZ, maxZ: f.minZ + backThickness }
      : { minX: left, maxX: right, minY: 0, maxY: plot.height, minZ: f.maxZ - backThickness, maxZ: f.maxZ })
  }
  return boxes
}

// ── Street furniture ──────────────────────────────────────────────────────
// Real natural sizes (measured from the GLBs), and the target height each
// should reach in metres. Kenney's kits do NOT share a unit convention, so a
// per-model scale is the only safe way to place them — hand-tuning at call
// sites is how you ship a two-metre-tall lamp post.

export interface StreetPropDef {
  file: string
  /** Measured natural bounding size in the model's own units. */
  natural: readonly [number, number, number]
  /** Real-world height this prop should stand at. */
  targetHeight: number
}

export const STREET_PROPS: Readonly<Record<string, StreetPropDef>> = {
  lamp: { file: 'street_lamp.glb', natural: [0.05, 0.675, 0.225], targetHeight: 5.0 },
  sign: { file: 'street_sign.glb', natural: [0.132, 0.707, 1.0], targetHeight: 2.6 },
  cone: { file: 'street_cone.glb', natural: [0.075, 0.094, 0.075], targetHeight: 0.5 },
  tree: { file: 'street_tree.glb', natural: [0.21, 0.767, 0.243], targetHeight: 6.0 },
  planter: { file: 'street_planter.glb', natural: [0.4, 0.177, 0.3], targetHeight: 0.8 },
}

/** Uniform scale that brings a prop to its real height. */
export function streetPropScale(def: StreetPropDef): number {
  return def.targetHeight / def.natural[1]
}

export interface StreetPropPlacement {
  id: string
  kind: keyof typeof STREET_PROPS
  x: number
  z: number
  rotY: number
}

/**
 * Furniture goes in the zone between the kerb and the building line — never in
 * the walking route, and never in the carriageway. Lamp posts are staggered
 * across the two sides rather than paired, and the two tree rows are offset
 * from each other so the canopy reads as a weave instead of a tunnel.
 */
export function computeStreetProps(): StreetPropPlacement[] {
  const out: StreetPropPlacement[] = []
  // 0.6 m back from the kerb face, which is where a real column sits.
  const northZ = CARRIAGEWAY_MIN_Z - 0.6
  const southZ = CARRIAGEWAY_MAX_Z + 0.6

  // Lamps, roughly 26 m apart per side, staggered by half a span. The offset
  // deliberately lands between destination doors; the final clearance filter
  // below remains the fail-safe when plots move.
  for (let x = HIGH_ST_MIN_X + 2; x <= HIGH_ST_MAX_X - 4; x += 26) {
    out.push({ id: `lamp-n-${x}`, kind: 'lamp', x, z: northZ, rotY: 0 })
  }
  for (let x = HIGH_ST_MIN_X + 15; x <= HIGH_ST_MAX_X - 4; x += 26) {
    out.push({ id: `lamp-s-${x}`, kind: 'lamp', x, z: southZ, rotY: 180 })
  }

  // Trees, 9 m apart, the south row offset half a span from the north.
  for (let x = HIGH_ST_MIN_X + 4; x <= HIGH_ST_MAX_X - 4; x += 9) {
    out.push({ id: `tree-n-${x}`, kind: 'tree', x, z: northZ - 0.4, rotY: (x * 37) % 360 })
  }
  for (let x = HIGH_ST_MIN_X + 8.5; x <= HIGH_ST_MAX_X - 4; x += 9) {
    // Skip the stem mouth — a tree there would block the junction sightline.
    if (x > STEM_MIN_X - 3 && x < STEM_MAX_X + 3) continue
    // Keep the Library/Datacentre seam open: the former tree at x=15.5
    // bisected both destination signs in every south-east context view.
    if (x > 12) continue
    out.push({ id: `tree-s-${x}`, kind: 'tree', x, z: southZ + 0.4, rotY: (x * 53) % 360 })
  }

  // Planters defining the stem's pedestrian edge.
  for (let z = STEM_MIN_Z + 3; z < STEM_MAX_Z - 2; z += 5) {
    out.push({ id: `planter-w-${z}`, kind: 'planter', x: STEM_MIN_X + 1.2, z, rotY: 90 })
    out.push({ id: `planter-e-${z}`, kind: 'planter', x: STEM_MAX_X - 1.2, z, rotY: 270 })
  }

  // A little honest disorder near the workshop.
  out.push({ id: 'cone-a', kind: 'cone', x: 27.5, z: northZ - 1.1, rotY: 0 })
  out.push({ id: 'cone-b', kind: 'cone', x: 28.9, z: northZ - 0.7, rotY: 0 })

  // Keep every authored entrance legible and traversable all the way from the
  // kerb. Account for the substantial prop's footprint, not only its origin.
  const approaches = PLOTS.filter((plot) => plot.enterable).map((plot) => plotApproachRect(plot))
  return out.filter((prop) => {
    const radius = prop.kind === 'planter' ? 0.6 : prop.kind === 'tree' ? 0.35 : prop.kind === 'lamp' ? 0.3 : 0.1
    return approaches.every((rect) =>
      prop.x + radius < rect.minX ||
      prop.x - radius > rect.maxX ||
      prop.z + radius < rect.minZ ||
      prop.z - radius > rect.maxZ,
    )
  })
}

// ── Ground surfacing ──────────────────────────────────────────────────────

export type SurfaceKind = 'carriageway' | 'footway' | 'apron'

export interface SurfacePatch {
  id: string
  kind: SurfaceKind
  rect: Rect3
}

/**
 * Long geometry is effectively never frustum-culled — the engine tests a
 * bounding SPHERE derived from the AABB, so a 76 m strip has a ~38 m radius
 * and is drawn from anywhere in the district. Everything long is therefore
 * emitted in segments no longer than this.
 */
export const MAX_SURFACE_SEGMENT = 16

/** Split a rect into segments along X so each stays frustum-cullable. */
export function segmentAlongX(id: string, kind: SurfaceKind, rect: Rect3): SurfacePatch[] {
  const span = rect.maxX - rect.minX
  const count = Math.max(1, Math.ceil(span / MAX_SURFACE_SEGMENT))
  const step = span / count
  return Array.from({ length: count }, (_, i) => ({
    id: `${id}-${i}`,
    kind,
    rect: {
      minX: rect.minX + i * step,
      maxX: rect.minX + (i + 1) * step,
      minZ: rect.minZ,
      maxZ: rect.maxZ,
    },
  }))
}

/** Split a rect into segments along Z. */
export function segmentAlongZ(id: string, kind: SurfaceKind, rect: Rect3): SurfacePatch[] {
  const span = rect.maxZ - rect.minZ
  const count = Math.max(1, Math.ceil(span / MAX_SURFACE_SEGMENT))
  const step = span / count
  return Array.from({ length: count }, (_, i) => ({
    id: `${id}-${i}`,
    kind,
    rect: {
      minX: rect.minX,
      maxX: rect.maxX,
      minZ: rect.minZ + i * step,
      maxZ: rect.minZ + (i + 1) * step,
    },
  }))
}

/** Every ground patch in the district, pre-segmented for culling. */
export function computeSurfaces(): SurfacePatch[] {
  const out: SurfacePatch[] = []

  out.push(...segmentAlongX('hs-carriageway', 'carriageway', {
    minX: HIGH_ST_MIN_X, maxX: HIGH_ST_MAX_X,
    minZ: CARRIAGEWAY_MIN_Z, maxZ: CARRIAGEWAY_MAX_Z,
  }))
  out.push(...segmentAlongX('hs-footway-n', 'footway', {
    minX: HIGH_ST_MIN_X, maxX: HIGH_ST_MAX_X,
    minZ: HIGH_ST_NORTH_FACE, maxZ: CARRIAGEWAY_MIN_Z,
  }))
  out.push(...segmentAlongX('hs-footway-s', 'footway', {
    minX: HIGH_ST_MIN_X, maxX: HIGH_ST_MAX_X,
    minZ: CARRIAGEWAY_MAX_Z, maxZ: HIGH_ST_SOUTH_FACE,
  }))

  // The stem is pedestrian-priority: one continuous surface, no kerbs.
  out.push(...segmentAlongZ('stem', 'apron', {
    minX: STEM_MIN_X, maxX: STEM_MAX_X,
    minZ: STEM_MIN_Z, maxZ: STEM_MAX_Z,
  }))

  return out
}

// ── Kerb runs ─────────────────────────────────────────────────────────────

export interface KerbRun {
  id: string
  /** Along-street extent. */
  minX: number
  maxX: number
  /** Z of the kerb face. */
  z: number
}

/**
 * Kerbs bound the carriageway on both sides, broken where the stem meets the
 * street (a pedestrian-priority arm has no kerb across its mouth).
 */
export function computeKerbs(): KerbRun[] {
  return [
    { id: 'kerb-n', minX: HIGH_ST_MIN_X, maxX: HIGH_ST_MAX_X, z: CARRIAGEWAY_MIN_Z },
    { id: 'kerb-s-west', minX: HIGH_ST_MIN_X, maxX: STEM_MIN_X, z: CARRIAGEWAY_MAX_Z },
    { id: 'kerb-s-east', minX: STEM_MAX_X, maxX: HIGH_ST_MAX_X, z: CARRIAGEWAY_MAX_Z },
  ]
}

// ── Spawn ─────────────────────────────────────────────────────────────────

/**
 * You arrive at the gate, looking north up the stem. From here the stem walls
 * frame the anchor almost exactly — it fills the opening with a sliver of
 * daylight each side, which is the one deliberately composed view in the
 * district and the reason the T-junction beats a straight street.
 */
export const DISTRICT_SPAWN = { x: 0, z: GATE_Z - 2, yaw: 0 } as const

/** Distance from the gate to the anchor's front door. */
export function gateToAnchorDistance(): number {
  const anchor = PLOTS.find((p) => p.id === ANCHOR_PLOT_ID)
  if (!anchor) return 0
  return GATE_Z - plotFrontZ(anchor)
}
